/**
 * @typedef TrayLanguage
 * @property {string} code - The ISO code of the language.
 * @property {string} name - The display name of the language.
 * @property {string} rootUrl - The root URL for the language.
 */

/**
 * @typedef TrayCountry
 * @property {string} code - The ISO code of the country.
 * @property {string} label - The display label of the country option.
 * @property {string} flag - The URL of the country flag image.
 * @property {string} levers - The HTML of the market levers.
 * @property {TrayLanguage[]} languages - The languages available for the country.
 */

/**
 * @typedef TrayRegion
 * @property {string} name - The name of the region.
 * @property {TrayCountry[]} countries - The countries in the region.
 */

/**
 * @typedef TrayFormData
 * @property {string} currentRegion - The currently selected region.
 * @property {string} currentCountry - The ISO code of the currently selected country.
 * @property {string} currentLanguage - The ISO code of the currently selected language.
 * @property {TrayRegion[]} regions - The available regions and their countries.
 */

/**
 * @typedef Suggestion
 * @property {object} parts - The parts of the suggestion.
 * @property {object} parts.country - The country part of the suggestion.
 * @property {string} parts.country.handle - The ISO code of the country.
 * @property {string} parts.country.name - The name of the country.
 */

/**
 * @typedef {HTMLElement & { selectCountry: function(string): void }} LocalizationTrayForm
 */

/**
 * @param {TrayFormData} data - The localization tray form data.
 * @param {string} countryCode - The ISO code of the country to find.
 * @returns {TrayCountry | undefined} The matching country, if found.
 */
function findTrayCountry(data, countryCode) {
  for (const region of data.regions) {
    const country = region.countries.find((entry) => entry.code === countryCode);

    if (country) return country;
  }

  return undefined;
}

/**
 * @param {TrayFormData} data - The localization tray form data.
 * @param {string} languageCode - The ISO code of the language to find.
 * @returns {TrayLanguage | undefined} The matching language, if found.
 */
function findTrayLanguage(data, languageCode) {
  for (const region of data.regions) {
    for (const country of region.countries) {
      const language = country.languages.find((entry) => entry.code === languageCode);

      if (language) return language;
    }
  }

  return undefined;
}

/**
 * @param {string} countryCode - The ISO code of the selected country.
 * @param {HTMLSelectElement} languageSelect - The language select element to update.
 * @param {TrayFormData} data - The localization tray form data.
 */
function updateTrayLanguages(countryCode, languageSelect, data) {
  const country = findTrayCountry(data, countryCode);
  const previousValue = languageSelect.value;

  languageSelect.innerHTML = '';

  if (!country?.languages.length) return;

  for (const language of country.languages) {
    languageSelect.add(new Option(language.name, language.code));
  }

  if (country.languages.some((language) => language.code === previousValue)) {
    languageSelect.value = previousValue;
    return;
  }

  if (country.languages.some((language) => language.code === data.currentLanguage)) {
    languageSelect.value = data.currentLanguage;
  }
}

/**
 * @param {string} countryCode - The ISO code of the selected country.
 * @param {TrayFormData} data - The localization tray form data.
 * @param {HTMLImageElement | null} countryFlag - The country flag image element to update.
 */
function updateTrayCountryFlag(countryCode, data, countryFlag) {
  if (!countryFlag) return;

  const country = findTrayCountry(data, countryCode);

  if (country?.flag) {
    countryFlag.src = country.flag;
  }
}

/**
 * @param {string} countryCode - The ISO code of the selected country.
 * @param {TrayFormData} data - The localization tray form data.
 * @param {HTMLElement | null} leversContainer - The container for market levers content.
 */
function updateTrayMarketLevers(countryCode, data, leversContainer) {
  if (!leversContainer) return;

  const country = findTrayCountry(data, countryCode);

  leversContainer.innerHTML = country?.levers || '';
}

/**
 * @param {string} regionName - The name of the selected region. Leave empty to include all countries.
 * @param {HTMLSelectElement} countrySelect - The country select element to update.
 * @param {HTMLSelectElement} languageSelect - The language select element to update.
 * @param {TrayFormData} data - The localization tray form data.
 * @param {HTMLImageElement | null} countryFlag - The country flag image element to update.
 * @param {HTMLElement | null} leversContainer - The container for market levers content.
 * @param {string} [preferredCountry] - The country to select after repopulating the options.
 */
function updateTrayCountries(
  regionName,
  countrySelect,
  languageSelect,
  data,
  countryFlag,
  leversContainer,
  preferredCountry = '',
) {
  const countries = regionName
    ? data.regions.find((entry) => entry.name === regionName)?.countries
    : data.regions.flatMap((region) => region.countries);

  if (!countries?.length) return;

  const previousCountry = countrySelect.value;

  countrySelect.innerHTML = '';

  for (const country of countries) {
    countrySelect.add(new Option(country.label, country.code));
  }

  if (countries.some((country) => country.code === preferredCountry)) {
    countrySelect.value = preferredCountry;
  } else if (countries.some((country) => country.code === previousCountry)) {
    countrySelect.value = previousCountry;
  } else if (countries.some((country) => country.code === data.currentCountry)) {
    countrySelect.value = data.currentCountry;
  } else {
    countrySelect.value = countries[0].code;
  }

  updateTrayLanguages(countrySelect.value, languageSelect, data);
  updateTrayCountryFlag(countrySelect.value, data, countryFlag);
  updateTrayMarketLevers(countrySelect.value, data, leversContainer);
}

if (!customElements.get('localization-tray-form')) {
  customElements.define(
    'localization-tray-form',
    class LocalizationTrayForm extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLScriptElement | null} */
        this.json = null;

        /** @type {HTMLSelectElement | null} */
        this.regionSelect = null;

        /** @type {HTMLSelectElement | null} */
        this.countrySelect = null;

        /** @type {HTMLSelectElement | null} */
        this.languageSelect = null;

        /** @type {HTMLFormElement | null} */
        this.form = null;

        /** @type {HTMLImageElement | null} */
        this.countryFlag = null;

        /** @type {HTMLElement | null} */
        this.leversContainer = null;

        /** @type {TrayFormData | null} */
        this.data = null;

        /** @type {AbortController | null} */
        this.languageRefreshController = null;

        this.currentLanguageCode = '';

        this.hideRegion = false;
      }

      connectedCallback() {
        /** @type {HTMLScriptElement | null} */
        this.json = this.querySelector('[data-localization-tray-json]');

        /** @type {HTMLSelectElement | null} */
        this.regionSelect = this.querySelector('[data-localization-tray-region]');

        /** @type {HTMLSelectElement | null} */
        this.countrySelect = this.querySelector('[data-localization-tray-country]');

        /** @type {HTMLSelectElement | null} */
        this.languageSelect = this.querySelector('[data-localization-tray-language]');

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form');

        /** @type {HTMLImageElement | null} */
        this.countryFlag = this.querySelector('[data-localization-tray-country-flag]');

        /** @type {HTMLElement | null} */
        this.leversContainer = this.querySelector('[data-localization-tray-levers]');

        if (!this.json || !this.countrySelect || !this.languageSelect) return;

        /** @type {TrayFormData} */
        const trayData = JSON.parse(this.json.textContent || '{}');
        this.data = trayData;
        this.currentLanguageCode = this.languageSelect.value;

        this.hideRegion = Boolean(this.closest('[data-localization-tray-hide-region]'));

        if (!this.hideRegion && this.regionSelect) {
          const regionSelect = this.regionSelect;
          const countrySelect = this.countrySelect;
          const languageSelect = this.languageSelect;
          const countryFlag = this.countryFlag;
          const leversContainer = this.leversContainer;

          regionSelect.addEventListener('change', () => {
            const data = this.data || trayData;

            updateTrayCountries(regionSelect.value, countrySelect, languageSelect, data, countryFlag, leversContainer);
            this.currentLanguageCode = languageSelect.value;
          });
        }

        const countrySelect = this.countrySelect;
        const languageSelect = this.languageSelect;
        const countryFlag = this.countryFlag;
        const leversContainer = this.leversContainer;

        countrySelect.addEventListener('change', () => {
          const data = this.data || trayData;

          updateTrayLanguages(countrySelect.value, languageSelect, data);
          updateTrayCountryFlag(countrySelect.value, data, countryFlag);
          updateTrayMarketLevers(countrySelect.value, data, leversContainer);
          this.currentLanguageCode = languageSelect.value;
        });

        languageSelect.addEventListener('change', () => {
          this.refreshCountriesForLanguage(languageSelect.value);
        });

        if (this.form) {
          this.form.addEventListener('submit', () => {
            localStorage.setItem('viewed-localization-banner', 'true');
          });
        }
      }

      /**
       * @param {string} languageCode - The ISO code of the selected language.
       */
      async refreshCountriesForLanguage(languageCode) {
        if (!this.data || !this.countrySelect || !this.languageSelect) return;

        const previousState = {
          data: this.data,
          countryCode: this.countrySelect.value,
          languageCode: this.currentLanguageCode || this.languageSelect.value,
          regionName: this.regionSelect?.value || this.data.currentRegion,
          regionOptions: this.regionSelect?.innerHTML || '',
        };
        const language = findTrayLanguage(this.data, languageCode);

        if (!language) {
          this.restoreLanguageRefreshState(previousState);

          return;
        }

        this.languageRefreshController?.abort();
        this.languageRefreshController = new AbortController();

        try {
          const response = await fetch(new URL(language.rootUrl || '/', window.location.origin), {
            credentials: 'same-origin',
            signal: this.languageRefreshController.signal,
          });

          if (!response.ok) {
            this.restoreLanguageRefreshState(previousState);

            return;
          }

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');

          /** @type {HTMLScriptElement | null} */
          const nextJson = documentFragment.querySelector('[data-localization-tray-json]');

          if (!nextJson) {
            this.restoreLanguageRefreshState(previousState);

            return;
          }

          /** @type {TrayFormData} */
          const nextData = JSON.parse(nextJson.textContent || '{}');
          const selectedCountry = this.countrySelect.value;
          const selectedRegion = this.regionSelect?.value || nextData.currentRegion;

          this.data = nextData;

          if (this.regionSelect) {
            /** @type {HTMLSelectElement | null} */
            const nextRegionSelect = documentFragment.querySelector('[data-localization-tray-region]');

            if (nextRegionSelect) {
              this.regionSelect.innerHTML = nextRegionSelect.innerHTML;
            }
          }

          const regionName = !this.hideRegion && this.regionSelect ? selectedRegion : '';
          const regionExists = nextData.regions.some((region) => region.name === regionName);
          const nextRegionName = regionExists ? regionName : nextData.currentRegion;

          if (!this.hideRegion && this.regionSelect) {
            this.regionSelect.value = nextRegionName;
          }

          updateTrayCountries(
            !this.hideRegion ? nextRegionName : '',
            this.countrySelect,
            this.languageSelect,
            nextData,
            this.countryFlag,
            this.leversContainer,
            selectedCountry,
          );

          if (this.languageSelect.querySelector(`option[value="${CSS.escape(languageCode)}"]`)) {
            this.languageSelect.value = languageCode;
          }

          this.currentLanguageCode = this.languageSelect.value;
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') return;

          this.restoreLanguageRefreshState(previousState);
        }
      }

      /**
       * @param {{ data: TrayFormData, countryCode: string, languageCode: string, regionName: string, regionOptions: string }} state - The last consistent tray state.
       */
      restoreLanguageRefreshState(state) {
        if (!this.countrySelect || !this.languageSelect) return;

        this.data = state.data;

        if (!this.hideRegion && this.regionSelect) {
          this.regionSelect.innerHTML = state.regionOptions;
          this.regionSelect.value = state.regionName;
        }

        updateTrayCountries(
          !this.hideRegion ? state.regionName : '',
          this.countrySelect,
          this.languageSelect,
          state.data,
          this.countryFlag,
          this.leversContainer,
          state.countryCode,
        );

        if (this.languageSelect.querySelector(`option[value="${CSS.escape(state.languageCode)}"]`)) {
          this.languageSelect.value = state.languageCode;
        }

        this.currentLanguageCode = this.languageSelect.value;
      }

      /**
       * @param {string} countryCode - The ISO code of the country to select.
       */
      selectCountry(countryCode) {
        if (!this.data || !this.countrySelect || !this.languageSelect) return;

        const data = this.data;
        const countrySelect = this.countrySelect;
        const languageSelect = this.languageSelect;
        const countryFlag = this.countryFlag;
        const leversContainer = this.leversContainer;
        const country = findTrayCountry(data, countryCode);

        if (!country) return;

        if (this.hideRegion) {
          countrySelect.value = countryCode;
          updateTrayLanguages(countryCode, languageSelect, data);
          updateTrayCountryFlag(countryCode, data, countryFlag);
          updateTrayMarketLevers(countryCode, data, leversContainer);
          this.currentLanguageCode = languageSelect.value;

          return;
        }

        const region = data.regions.find((entry) =>
          entry.countries.some((entryCountry) => entryCountry.code === countryCode),
        );

        if (!region) return;

        if (this.regionSelect) {
          this.regionSelect.value = region.name;
        }

        updateTrayCountries(
          region.name,
          countrySelect,
          languageSelect,
          data,
          countryFlag,
          leversContainer,
          countryCode,
        );
        this.currentLanguageCode = languageSelect.value;
      }
    },
  );
}

if (!customElements.get('localization-banner')) {
  customElements.define(
    'localization-banner',
    class LocalizationBanner extends HTMLElement {
      constructor() {
        super();

        this.trap = window.traps.store['localization-tray'];

        this.suggestion = null;
      }

      async connectedCallback() {
        if (localStorage.getItem('viewed-localization-banner') === 'true') {
          window.dispatchEvent(new CustomEvent('localization-info-activate'));

          return;
        }

        const suggestion = await this.getSuggestedMarket();

        if (suggestion) {
          this.suggestion = suggestion;

          this.selectSuggestedCountry(suggestion);

          if (this.trap) {
            if (window.traps.store['cookies'].active) {
              window.addEventListener(
                'modal-trap-deactivate',
                (e) => {
                  if (e.detail.id === 'cookies') {
                    this.trap.activate();
                  }
                },
                { once: true },
              );
            } else {
              this.trap.activate();
            }

            window.setCookie('viewed-localization-content', 'true', 24);
            localStorage.setItem('viewed-localization-banner', 'true');
          }
        } else {
          if (window.traps.store['cookies'].active) {
            window.addEventListener('modal-trap-deactivate', (e) => {
              if (e.detail.id === 'cookies') {
                window.dispatchEvent(new CustomEvent('localization-info-activate'));
              }
            });
          } else {
            window.dispatchEvent(new CustomEvent('localization-info-activate'));
          }
        }
      }

      async getSuggestedMarket() {
        const response = await fetch(
          window.Shopify.routes.root +
            'browsing_context_suggestions.json' +
            '?country[enabled]=true' +
            `&country[exclude]=${window.Shopify.country}`,
        );

        if (!response.ok) {
          throw new Error(response.statusText);
        }

        const data = await response.json();

        if (response.ok) {
          const suggestions = data.suggestions;

          if (suggestions.length > 0) return suggestions[0];

          return null;
        }
      }

      /**
       * @param {Suggestion} suggestion - The market suggestions from the API.
       */
      selectSuggestedCountry(suggestion) {
        const country = suggestion.parts?.country;

        if (!country) return;

        const tray = this.closest('[data-focus-trap="localization-tray"]');

        if (!tray) return;

        /** @type {LocalizationTrayForm | null} */
        const trayForm = tray.querySelector('localization-tray-form');

        trayForm?.selectCountry(country.handle);
      }
    },
  );
}
